// ============================================
// Command-K Search Modal Component
// ============================================
// Global search modal activated with Cmd/Ctrl+K
// Features: fuzzy search, keyboard navigation, grouped results

import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import { useCommandSearchStore, type SearchEntityType, type SearchResult } from '@/stores';
import { cn } from '@/lib/utils';

// ============================================
// Type Icons
// ============================================

const TypeIcons: Record<SearchEntityType, React.FC<{ className?: string }>> = {
  lead: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
      />
    </svg>
  ),
  account: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  ),
  contact: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  ),
  opportunity: ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
      />
    </svg>
  ),
};

const TypeLabels: Record<SearchEntityType, string> = {
  lead: 'Leads',
  account: 'Accounts',
  contact: 'Contacts',
  opportunity: 'Opportunities',
};

const TypeColors: Record<SearchEntityType, string> = {
  lead: 'text-blue-500',
  account: 'text-amber-500',
  contact: 'text-emerald-500',
  opportunity: 'text-purple-500',
};

// ============================================
// Command Search Component
// ============================================

export function CommandSearch() {
  const { isOpen, open, close, toggle } = useCommandSearchStore();
  const { query, setQuery, results, isLoading, isIndexLoading } = useGlobalSearch({ limit: 12 });
  const navigate = useNavigate();

  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  // Flatten results for keyboard navigation
  const flatResults = React.useMemo(() => {
    return results.map(r => r.item);
  }, [results]);

  // Reset selection when results change
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Global keyboard shortcut (Cmd/Ctrl+K)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  // Focus input when opening
  React.useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the modal is mounted
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen, setQuery]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < flatResults.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (flatResults[selectedIndex]) {
          handleSelect(flatResults[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
    }
  };

  // Scroll selected item into view
  React.useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleSelect = (item: SearchResult['item']) => {
    close();
    // Type assertion needed: url is a string from search index, but navigate expects typed route
    navigate({ to: item.url as '/' });
  };

  // Group results by type
  const groupedResults = React.useMemo(() => {
    const groups: Record<SearchEntityType, SearchResult['item'][]> = {
      lead: [],
      account: [],
      contact: [],
      opportunity: [],
    };
    flatResults.forEach(item => {
      groups[item.type].push(item);
    });
    return groups;
  }, [flatResults]);

  // Get flat index for an item
  const getFlatIndex = (type: SearchEntityType, indexInGroup: number): number => {
    const order: SearchEntityType[] = ['lead', 'account', 'contact', 'opportunity'];
    let flatIndex = 0;
    for (const t of order) {
      if (t === type) {
        return flatIndex + indexInGroup;
      }
      flatIndex += groupedResults[t].length;
    }
    return flatIndex;
  };

  if (!isOpen) {
    return (
      <button
        onClick={open}
        className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors w-full max-w-xs"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <span className="flex-1 text-left">Search...</span>
        <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>
    );
  }

  return (
    <>
      {/* Trigger button (hidden when modal is open) */}
      <button onClick={open} className="hidden" aria-hidden="true" />

      {/* Modal Backdrop */}
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
        <div className="fixed inset-0 bg-black/50 animate-in fade-in-0" onClick={close} />

        {/* Modal Content */}
        <div
          className="relative z-50 w-full max-w-lg bg-background border border-border rounded-xl shadow-2xl animate-in fade-in-0 zoom-in-95"
          onKeyDown={handleKeyDown}
        >
          {/* Search Input */}
          <div className="flex items-center border-b border-border px-4">
            <svg
              className="w-5 h-5 text-muted-foreground shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search leads, accounts, contacts, opportunities..."
              className="flex-1 h-14 px-3 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
            {isLoading && (
              <svg
                className="w-5 h-5 text-muted-foreground animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            )}
            <kbd className="hidden sm:inline-flex h-5 items-center rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground ml-2">
              ESC
            </kbd>
          </div>

          {/* Results List */}
          <div ref={listRef} className="max-h-80 overflow-y-auto p-2">
            {isIndexLoading && !flatResults.length && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                Loading search index...
              </div>
            )}

            {!isLoading && query.length >= 2 && flatResults.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No results found for "{query}"
              </div>
            )}

            {!isLoading && query.length > 0 && query.length < 2 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                Type at least 2 characters to search
              </div>
            )}

            {query.length === 0 && flatResults.length > 0 && (
              <div className="px-4 py-2 text-xs text-muted-foreground">Recent items</div>
            )}

            {/* Grouped Results */}
            {(['lead', 'account', 'contact', 'opportunity'] as SearchEntityType[]).map(type => {
              const items = groupedResults[type];
              if (items.length === 0) return null;

              const Icon = TypeIcons[type];

              return (
                <div key={type} className="mb-2">
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
                    {TypeLabels[type]}
                  </div>
                  {items.map((item, indexInGroup) => {
                    const flatIndex = getFlatIndex(type, indexInGroup);
                    const isSelected = flatIndex === selectedIndex;

                    return (
                      <button
                        key={item.id}
                        data-index={flatIndex}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setSelectedIndex(flatIndex)}
                        className={cn(
                          'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left transition-colors',
                          isSelected ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                        )}
                      >
                        <Icon className={cn('w-5 h-5 shrink-0', TypeColors[type])} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{item.title}</div>
                          {item.subtitle && (
                            <div className="text-xs text-muted-foreground truncate">
                              {item.subtitle}
                            </div>
                          )}
                        </div>
                        {item.status && (
                          <span className="text-xs text-muted-foreground shrink-0">
                            {item.status}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="inline-flex items-center justify-center h-5 w-5 rounded border bg-muted font-mono text-[10px]">
                  ↑
                </kbd>
                <kbd className="inline-flex items-center justify-center h-5 w-5 rounded border bg-muted font-mono text-[10px]">
                  ↓
                </kbd>
                <span>Navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="inline-flex items-center justify-center h-5 px-1.5 rounded border bg-muted font-mono text-[10px]">
                  ↵
                </kbd>
                <span>Open</span>
              </span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="inline-flex items-center justify-center h-5 px-1.5 rounded border bg-muted font-mono text-[10px]">
                ESC
              </kbd>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
